/**
 * Dynamic PWA manifest helper.
 *
 * The server exposes /manifest/:role/:jwt which returns a manifest with the
 * correct start_url for that user. Updating the <link rel="manifest"> href
 * to this real same-origin URL lets Chrome/Safari actually detect the manifest
 * (blob: URLs are rejected for web app manifests by all major browsers).
 */

const PWA_MANIFEST_HREF_KEY = "pwa_manifest_href";

/**
 * Derive the manifest href from the current page URL.
 * Maps /admin/:jwt → /manifest/admin/:jwt  and /client/:jwt → /manifest/client/:jwt.
 */
export function deriveManifestHref(): string {
  const path = window.location.pathname;
  const adminMatch = path.match(/^\/admin\/([^/]+)/);
  const clientMatch = path.match(/^\/client\/([^/]+)/);
  if (adminMatch?.[1]) return `/manifest/admin/${adminMatch[1]}`;
  if (clientMatch?.[1]) return `/manifest/client/${clientMatch[1]}`;
  // Fall back to any previously saved href (e.g. cold-start at "/")
  return localStorage.getItem(PWA_MANIFEST_HREF_KEY) ?? "/manifest.json";
}

/** @deprecated alias — kept for existing callers */
export const deriveStartUrl = deriveManifestHref;

/**
 * Point the <link rel="manifest"> at the role-specific server endpoint so
 * the browser always sees the correct start_url when offering installation.
 */
export function updateManifestStartUrl(href: string): void {
  let link = document.querySelector(
    'link[rel="manifest"]',
  ) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }

  // Always set — the static HTML has an empty href placeholder
  link.href = href;

  // Persist so future cold-starts at "/" still get the right manifest
  if (href !== "/manifest.json") {
    localStorage.setItem(PWA_MANIFEST_HREF_KEY, href);
  }
}
